import { useState, useEffect, useMemo } from "react";
import { PageHeader } from "@/components/Primitives";
import { StatusChip } from "@/components/StatusChip";
import { fmtDateTime } from "@/lib/format";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Phone, MapPinned, RefreshCw, Users2, CheckSquare } from "lucide-react";
import { cn } from "@/lib/utils";

const TYPE = {
  Meeting: { c: "#A78BFA", icon: Users2 }, Call: { c: "#00C9E0", icon: Phone },
  "Follow-Up": { c: "#F97316", icon: CheckSquare }, Visit: { c: "#9ACC32", icon: MapPinned },
  Renewal: { c: "#FE3967", icon: RefreshCw }, Task: { c: "#00C9E0", icon: CheckSquare },
};
const VIEWS = ["Day", "Week", "Month", "Agenda"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HOURS = Array.from({ length: 14 }, (_, i) => i + 7); // 7am–8pm

const ymd = (d) => d.toISOString().slice(0, 10);
const startOfWeek = (d) => { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const sameDay = (a, b) => ymd(new Date(a)) === ymd(b);

export default function Calendar() {
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [sel, setSel] = useState(null);
  const [view, setView] = useState("Month");
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data.items));
    api.get("/tasks").then((r) => setTasks(r.data.items.filter((t) => t.status !== "Completed")));
  }, []);

  const items = useMemo(() => [
    ...events.map((e) => ({ id: e.id, date: e.start, title: e.title, type: e.type || "Meeting", sub: e.business_name, location: e.location })),
    ...tasks.map((t) => ({ id: "t-" + t.id, date: t.due_date, title: t.title, type: "Follow-Up", sub: t.business_name, priority: t.priority })),
  ].filter((i) => i.date).sort((a, b) => new Date(a.date) - new Date(b.date)), [events, tasks]);

  const forDay = (d) => items.filter((i) => sameDay(i.date, d));

  const nav = (dir) => {
    const step = { Day: 1, Week: 7, Month: 30, Agenda: 30 }[view];
    if (view === "Month") { const x = new Date(cursor); x.setMonth(x.getMonth() + dir); setCursor(x); }
    else setCursor(addDays(cursor, dir * step));
  };
  const today = () => { const d = new Date(); d.setHours(0, 0, 0, 0); setCursor(d); };

  const label = view === "Month"
    ? cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })
    : view === "Day" ? cursor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })
      : `${startOfWeek(cursor).toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${addDays(startOfWeek(cursor), 6).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;

  const Pill = ({ i }) => {
    const m = TYPE[i.type] || TYPE.Meeting;
    return (
      <button onClick={() => setSel(i)} data-testid={`cal-item-${i.id}`}
        className="w-full text-left rounded-md px-1.5 py-1 text-[11px] font-medium truncate hover:brightness-95 transition"
        style={{ background: `${m.c}1f`, color: m.c, borderLeft: `3px solid ${m.c}` }}>
        {new Date(i.date).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} {i.title}
      </button>
    );
  };

  const now = new Date(); now.setHours(0, 0, 0, 0);

  return (
    <div data-testid="page-calendar">
      <PageHeader title="Calendar" subtitle="Meetings, follow-ups and scheduled activities." />
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => nav(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" className="h-8" onClick={today} data-testid="cal-today">Today</Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => nav(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <h2 className="font-display font-semibold text-slate-800 text-lg">{label}</h2>
        <div className="sm:ml-auto inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
          {VIEWS.map((v) => (
            <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v.toLowerCase()}`}
              className={cn("px-3 py-1.5 text-sm font-medium rounded-md transition", view === v ? "bg-[#00C9E0] text-white" : "text-slate-500 hover:text-slate-800")}>{v}</button>
          ))}
        </div>
      </div>

      {view === "Month" && (() => {
        const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
        const gridStart = startOfWeek(first);
        const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
        return (
          <div className="cc-card overflow-hidden" data-testid="cal-month">
            <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50/60">
              {DOW.map((d) => <div key={d} className="px-2 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-400 text-center">{d}</div>)}
            </div>
            <div className="grid grid-cols-7">
              {days.map((d, idx) => {
                const inMonth = d.getMonth() === cursor.getMonth();
                const dayItems = forDay(d);
                const isToday = sameDay(d, now);
                return (
                  <div key={idx} className={cn("min-h-[92px] border-b border-r border-slate-100 p-1.5 space-y-1", !inMonth && "bg-slate-50/40")}>
                    <div className={cn("flex items-center justify-center h-6 w-6 rounded-full text-xs font-semibold mx-auto", isToday ? "bg-[#00C9E0] text-white" : inMonth ? "text-slate-700" : "text-slate-300")}>{d.getDate()}</div>
                    {dayItems.slice(0, 3).map((i) => <Pill key={i.id} i={i} />)}
                    {dayItems.length > 3 && <p className="text-[10px] text-slate-400 pl-1">+{dayItems.length - 3} more</p>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {view === "Week" && (() => {
        const ws = startOfWeek(cursor);
        const days = Array.from({ length: 7 }, (_, i) => addDays(ws, i));
        return (
          <div className="cc-card overflow-x-auto" data-testid="cal-week">
            <div className="grid grid-cols-7 min-w-[720px]">
              {days.map((d, i) => (
                <div key={i} className="border-r border-slate-100 last:border-r-0">
                  <div className={cn("text-center py-2 border-b border-slate-100", sameDay(d, now) && "bg-[#00C9E0]/5")}>
                    <p className="text-[11px] uppercase text-slate-400 font-semibold">{DOW[d.getDay()]}</p>
                    <p className={cn("text-sm font-bold", sameDay(d, now) ? "text-[#0094A8]" : "text-slate-700")}>{d.getDate()}</p>
                  </div>
                  <div className="p-1.5 space-y-1 min-h-[300px]">{forDay(d).map((i) => <Pill key={i.id} i={i} />)}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {view === "Day" && (
        <div className="cc-card overflow-hidden" data-testid="cal-day">
          {HOURS.map((h) => {
            const slot = items.filter((i) => sameDay(i.date, cursor) && new Date(i.date).getHours() === h);
            return (
              <div key={h} className="flex border-b border-slate-100 last:border-b-0 min-h-[52px]">
                <div className="w-20 shrink-0 px-3 py-2 text-xs text-slate-400 text-right border-r border-slate-100">{h % 12 || 12}{h < 12 ? "am" : "pm"}</div>
                <div className="flex-1 p-1.5 space-y-1">{slot.map((i) => <Pill key={i.id} i={i} />)}</div>
              </div>
            );
          })}
        </div>
      )}

      {view === "Agenda" && (
        <div className="space-y-5" data-testid="cal-agenda">
          {Object.entries(items.reduce((acc, i) => { const d = (i.date || "").slice(0, 10); (acc[d] = acc[d] || []).push(i); return acc; }, {})).map(([date, list]) => (
            <div key={date}>
              <h3 className="font-display font-semibold text-sm text-slate-800 mb-2">{new Date(date).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</h3>
              <div className="cc-card divide-y divide-slate-100 overflow-hidden">
                {list.map((i) => {
                  const m = TYPE[i.type] || TYPE.Meeting; const Ic = m.icon;
                  return (
                    <button key={i.id} onClick={() => setSel(i)} className="w-full flex items-center justify-between p-4 text-left hover:bg-[#00C9E0]/[0.04]" data-testid={`cal-item-${i.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${m.c}1f` }}><Ic className="h-4 w-4" style={{ color: m.c }} /></span>
                        <div className="min-w-0"><p className="font-medium text-sm text-slate-800 truncate">{i.title}</p><p className="text-xs text-slate-400 truncate">{i.sub ? `${i.sub} · ` : ""}{fmtDateTime(i.date)}</p></div>
                      </div>
                      <span className="text-xs font-semibold rounded-full px-2.5 py-0.5 border" style={{ color: m.c, borderColor: `${m.c}55`, background: `${m.c}12` }}>{i.type}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent data-testid="cal-detail-dialog">
          <DialogHeader><DialogTitle className="font-display">{sel?.title}</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2"><span className="text-slate-400 w-24">Type</span><StatusChip value={sel.type} tone={sel.type === "Meeting" ? "violet" : sel.type === "Renewal" ? "pink" : "cyan"} /></div>
              <div className="flex items-center gap-2"><span className="text-slate-400 w-24">When</span><span className="text-slate-700 tnum">{fmtDateTime(sel.date)}</span></div>
              {sel.sub && <div className="flex items-center gap-2"><span className="text-slate-400 w-24">Business</span><span className="text-slate-700">{sel.sub}</span></div>}
              {sel.location && <div className="flex items-center gap-2"><span className="text-slate-400 w-24">Location</span><span className="text-slate-700">{sel.location}</span></div>}
              {sel.priority && <div className="flex items-center gap-2"><span className="text-slate-400 w-24">Priority</span><StatusChip value={sel.priority} /></div>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
